import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Typography,
  message,
} from 'antd'
import { API } from 'src/utils/interceptor'
import {
  findVenueProductForStockTakeProduct,
  getConversionRecordId,
  getConversionRecordsForStockProduct,
  mapConversionRecordsToOptions,
} from 'src/utils/stockTakeConversions'
import { unwrapStockTakeOriginalUom } from 'src/utils/stockTakeLineTitle'

const { Text } = Typography
const { Option, OptGroup } = Select

const EMPTY_EDITOR = {
  visible: false,
  mode: 'create',
  conversionId: '',
  label: '',
  toQty: '',
  toUnit: '',
  size: '',
  saving: false,
}

const ProductConversionModal = ({
  visible,
  stockTakeProduct,
  venueId,
  venueProducts = [],
  isStockTakeLocked = false,
  onClose,
  onConversionsUpdated,
  onApplyConversion,
}) => {
  const [resolvedVenueProductId, setResolvedVenueProductId] = useState('')
  const [localConversions, setLocalConversions] = useState([])
  const [applySelection, setApplySelection] = useState('__BASE_UOM__')
  const [isEnsuringVenueProduct, setIsEnsuringVenueProduct] = useState(false)
  const [conversionEditor, setConversionEditor] = useState(EMPTY_EDITOR)

  const matchedVenueProduct = useMemo(
    () =>
      findVenueProductForStockTakeProduct(stockTakeProduct, venueProducts) ||
      null,
    [stockTakeProduct, venueProducts],
  )

  const baseUom = unwrapStockTakeOriginalUom(stockTakeProduct?.UoM)
  const baseSize = String(
    stockTakeProduct?.size || matchedVenueProduct?.size || '',
  ).trim()

  const conversionOptions = useMemo(
    () => mapConversionRecordsToOptions(localConversions),
    [localConversions],
  )

  useEffect(() => {
    if (!visible || !stockTakeProduct) return

    const venueProductId = String(
      matchedVenueProduct?._id || stockTakeProduct?.venueProductId || '',
    ).trim()
    setResolvedVenueProductId(venueProductId)
    setLocalConversions(
      getConversionRecordsForStockProduct(stockTakeProduct, venueProducts),
    )
    setApplySelection(
      stockTakeProduct?.conversionId
        ? String(stockTakeProduct.conversionId)
        : '__BASE_UOM__',
    )
    setConversionEditor(EMPTY_EDITOR)
  }, [visible, stockTakeProduct, matchedVenueProduct, venueProducts])

  const ensureVenueProduct = async () => {
    const existingId = String(
      resolvedVenueProductId || matchedVenueProduct?._id || '',
    ).trim()
    if (existingId) return existingId
    if (!venueId || !stockTakeProduct) return ''

    setIsEnsuringVenueProduct(true)
    try {
      const createdVenueProduct = await API.post(
        'api',
        `venue/${venueId}/product`,
        {
          body: {
            venue: venueId,
            title: String(stockTakeProduct?.title || 'New Product').trim(),
            UoM: String(stockTakeProduct?.UoM || 'unit').trim(),
            supplierTitle: String(stockTakeProduct?.supplierTitle || '').trim(),
            type: String(stockTakeProduct?.type || 'Default Type').trim(),
            zone: 'Default Zone',
            defaultQuantity: 1,
            isFavourite: false,
            conversions: [],
          },
        },
      )
      const createdId = String(createdVenueProduct?._id || '').trim()
      if (!createdId) {
        throw new Error('venue_product_create_failed')
      }
      setResolvedVenueProductId(createdId)
      return createdId
    } catch (error) {
      console.error('Failed to ensure venue product for conversions:', error)
      message.error('Unable to link this product for conversions right now.')
      return ''
    } finally {
      setIsEnsuringVenueProduct(false)
    }
  }

  const syncAfterConversionChange = async (
    venueProductId,
    nextConversions,
    autoApplyConversionId = null,
  ) => {
    setLocalConversions(nextConversions)
    if (autoApplyConversionId) {
      setApplySelection(String(autoApplyConversionId))
    }
    if (onConversionsUpdated) {
      await onConversionsUpdated({
        stockTakeProduct,
        venueProductId,
        conversions: nextConversions,
        applyConversionId: autoApplyConversionId,
      })
    }
  }

  const openCreateEditor = () => {
    setConversionEditor({
      visible: true,
      mode: 'create',
      conversionId: '',
      label: '',
      toQty: '',
      toUnit: '',
      size: baseSize,
      saving: false,
    })
  }

  const openEditEditor = conversion => {
    setConversionEditor({
      visible: true,
      mode: 'edit',
      conversionId: getConversionRecordId(conversion),
      label: String(conversion?.label || '').trim(),
      toQty: Number(conversion?.toQty || 0),
      toUnit: String(conversion?.toUnit || '').trim(),
      size: String(conversion?.size || baseSize).trim(),
      saving: false,
    })
  }

  const handleSaveConversion = async () => {
    const venueProductId = await ensureVenueProduct()
    if (!venueId || !venueProductId) return

    const { mode, conversionId, label, toQty, toUnit, size } = conversionEditor
    if (
      !String(label || '').trim() ||
      Number(toQty) <= 0 ||
      !String(toUnit || '').trim()
    ) {
      message.error('Please enter label, qty (>0), and unit.')
      return
    }

    setConversionEditor(prev => ({ ...prev, saving: true }))
    try {
      const body = {
        label: String(label || '').trim(),
        toQty: Number(toQty),
        toUnit: String(toUnit || '').trim(),
        size: String(size || baseSize || '1').trim(),
      }
      const response =
        mode === 'edit' && conversionId
          ? await API.patch(
              'api2',
              `venue/${venueId}/products/${venueProductId}/conversions/${conversionId}`,
              { body },
            )
          : await API.post(
              'api2',
              `venue/${venueId}/products/${venueProductId}/conversions`,
              { body },
            )

      const nextConversions = Array.isArray(response?.venueProduct?.conversions)
        ? response.venueProduct.conversions
        : []
      const savedConversionId = String(
        response?.conversion?._id ||
          response?.conversion?.id ||
          conversionId ||
          '',
      ).trim()

      message.success(
        mode === 'edit' ? 'Conversion updated.' : 'Conversion created.',
      )
      setConversionEditor(EMPTY_EDITOR)
      await syncAfterConversionChange(
        venueProductId,
        nextConversions,
        mode === 'create' ? savedConversionId : null,
      )
    } catch (error) {
      console.error('Failed to save conversion:', error)
      message.error(
        error?.response?.data?.message || 'Failed to save conversion.',
      )
      setConversionEditor(prev => ({ ...prev, saving: false }))
    }
  }

  const handleDeleteConversion = async conversion => {
    const venueProductId = String(
      resolvedVenueProductId || matchedVenueProduct?._id || '',
    ).trim()
    const conversionId = getConversionRecordId(conversion)
    if (!venueId || !venueProductId || !conversionId) return

    try {
      const response = await API.del(
        'api2',
        `venue/${venueId}/products/${venueProductId}/conversions/${conversionId}`,
      )
      const nextConversions = Array.isArray(response?.venueProduct?.conversions)
        ? response.venueProduct.conversions
        : []
      if (applySelection === conversionId) {
        setApplySelection('__BASE_UOM__')
      }
      message.success('Conversion deleted.')
      await syncAfterConversionChange(venueProductId, nextConversions)
    } catch (error) {
      console.error('Failed to delete conversion:', error)
      message.error(
        error?.response?.data?.message || 'Failed to delete conversion.',
      )
    }
  }

  const handleApplyAndClose = async () => {
    if (!onApplyConversion || !stockTakeProduct) {
      onClose?.()
      return
    }

    if (applySelection === '__BASE_UOM__') {
      onApplyConversion(stockTakeProduct, null)
      onClose?.()
      return
    }

    const selectedOption = conversionOptions.find(
      option => option.value === applySelection,
    )
    if (!selectedOption) {
      message.error('Select a valid counting unit.')
      return
    }
    onApplyConversion(stockTakeProduct, selectedOption)
    onClose?.()
  }

  if (!stockTakeProduct) return null

  return (
    <>
      <Modal
        title='Manage unit conversions'
        visible={visible}
        onCancel={onClose}
        width={720}
        destroyOnClose
        footer={[
          <Button key='cancel' onClick={onClose}>
            Cancel
          </Button>,
          <Button
            key='apply'
            type='primary'
            disabled={isStockTakeLocked}
            onClick={handleApplyAndClose}
          >
            Apply to stock take
          </Button>,
        ]}
      >
        <Text type='secondary' style={{ display: 'block', marginBottom: 12 }}>
          Conversions are shared with Menu Builder and Link Products. Example:{' '}
          <strong>1 x 2.5kg = 2500 g</strong>.
        </Text>

        <div
          style={{
            border: '1px solid #f0f0f0',
            borderRadius: 8,
            padding: 10,
            marginBottom: 16,
            background: '#fafafa',
          }}
        >
          <Text strong>{stockTakeProduct?.title || 'Selected product'}</Text>
          <div>
            <Text type='secondary'>
              {`Base size: ${baseSize || 'N/A'} • Base UoM: ${baseUom}`}
              {stockTakeProduct?.supplierTitle
                ? ` • ${stockTakeProduct.supplierTitle}`
                : ''}
            </Text>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <Text strong>Existing conversions</Text>
            <Button
              size='small'
              type='primary'
              disabled={isStockTakeLocked || isEnsuringVenueProduct}
              onClick={openCreateEditor}
            >
              Add conversion
            </Button>
          </div>
          {localConversions.length === 0 ? (
            <Text type='secondary'>
              No conversions yet. Add one to count this product in a different
              unit during stock take.
            </Text>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {localConversions.map(conversion => {
                const conversionId = getConversionRecordId(conversion)
                return (
                  <div
                    key={conversionId}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 12,
                      alignItems: 'center',
                      border: '1px solid #efefef',
                      borderRadius: 8,
                      padding: '8px 10px',
                    }}
                  >
                    <div>
                      <Text strong>{conversion?.label || 'Conversion'}</Text>
                      <div>
                        <Text type='secondary'>
                          {`1 x ${conversion?.size || baseSize || '1'} → ${
                            conversion?.toQty
                          } ${conversion?.toUnit || ''}`}
                        </Text>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Button
                        size='small'
                        type='link'
                        disabled={isStockTakeLocked}
                        onClick={() => openEditEditor(conversion)}
                      >
                        Edit
                      </Button>
                      <Popconfirm
                        title='Delete this conversion?'
                        okText='Delete'
                        cancelText='Cancel'
                        disabled={isStockTakeLocked}
                        onConfirm={() => handleDeleteConversion(conversion)}
                      >
                        <Button size='small' type='link' danger>
                          Delete
                        </Button>
                      </Popconfirm>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div>
          <Text strong>Count in this stock take as</Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            value={applySelection}
            disabled={isStockTakeLocked}
            onChange={setApplySelection}
          >
            <Option value='__BASE_UOM__'>{baseUom}</Option>
            {conversionOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
          <Text type='secondary' style={{ display: 'block', marginTop: 8 }}>
            New conversions are applied to this row automatically. Use this
            dropdown to switch back to the original unit or another conversion.
          </Text>
        </div>
      </Modal>

      <Modal
        title={
          conversionEditor.mode === 'edit'
            ? 'Edit conversion'
            : 'Add conversion'
        }
        visible={conversionEditor.visible}
        onCancel={() => setConversionEditor(EMPTY_EDITOR)}
        onOk={handleSaveConversion}
        confirmLoading={conversionEditor.saving}
        destroyOnClose
      >
        <div style={{ display: 'grid', gap: 12 }}>
          <div>
            <Text strong>Label</Text>
            <Input
              style={{ marginTop: 6 }}
              value={conversionEditor.label}
              onChange={event =>
                setConversionEditor(prev => ({
                  ...prev,
                  label: event.target.value,
                }))
              }
              placeholder='e.g. 1x2.5kg => 2500 g'
            />
          </div>
          <div>
            <Text strong>Conversion rule</Text>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
                gap: 8,
                alignItems: 'center',
                marginTop: 6,
              }}
            >
              <Text type='secondary' style={{ marginBottom: 0 }}>
                From
              </Text>
              <InputNumber disabled value={1} style={{ width: '100%' }} />
              <Input
                disabled
                value={conversionEditor.size || baseSize}
                style={{ width: '100%' }}
              />
              <Text type='secondary' style={{ marginBottom: 0 }}>
                To
              </Text>
              <InputNumber
                style={{ width: '100%' }}
                min={0.0001}
                value={conversionEditor.toQty}
                placeholder='Qty'
                onChange={value =>
                  setConversionEditor(prev => ({
                    ...prev,
                    toQty: value === null || value === undefined ? '' : value,
                  }))
                }
              />
              <Select
                style={{ width: '100%' }}
                value={conversionEditor.toUnit || undefined}
                placeholder='Unit'
                dropdownMatchSelectWidth={false}
                dropdownStyle={{ minWidth: 220 }}
                onChange={value =>
                  setConversionEditor(prev => ({ ...prev, toUnit: value }))
                }
              >
                <OptGroup label='Weight'>
                  <Option value='kg'>Kilogram (kg)</Option>
                  <Option value='g'>Gram (g)</Option>
                  <Option value='mg'>Milligram (mg)</Option>
                  <Option value='lb'>Pound (lb)</Option>
                  <Option value='oz'>Ounce (oz)</Option>
                </OptGroup>
                <OptGroup label='Volume'>
                  <Option value='ltr'>Liter (ltr)</Option>
                  <Option value='ml'>Milliliter (ml)</Option>
                  <Option value='cl'>Centiliter (cl)</Option>
                  <Option value='gal'>Gallon (gal)</Option>
                  <Option value='floz'>Fluid Ounce (fl oz)</Option>
                </OptGroup>
                <OptGroup label='Count'>
                  <Option value='unit'>Unit</Option>
                  <Option value='piece'>Piece</Option>
                  <Option value='item'>Item</Option>
                  <Option value='pack'>Pack</Option>
                  <Option value='box'>Box</Option>
                </OptGroup>
              </Select>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default ProductConversionModal
